import { create } from 'zustand';

/**
 * The bulk import a user started in this session.
 *
 * The import itself runs server-side and its progress is persisted, so it survives the drawer
 * closing, the user navigating away, and the browser tab being idle. Before this store the *UI*
 * did not: the job id lived in BulkImportDrawer's own state and was thrown away on close, leaving
 * the user with no progress, no completion signal, and a second Import attempt that the server
 * rejects with "An import is already running for your company."
 *
 * Holding it here instead means the job outlives the drawer and the route. It is deliberately
 * in-memory only — no localStorage, no persist middleware (used nowhere in this codebase) — so a
 * refresh drops the indicator. The import still finishes; recovering it after a reload would need
 * a firm-scoped lookup the API does not expose (the closest, HasActiveJobAsync, returns a bool).
 */
export const useBulkImportJobStore = create((set) => ({
  // ────────── STATE ──────────

  /** Job being watched, or null when nothing is in flight. */
  jobId: null,

  /** Latest BulkUserImportStatusResponse from GET /api/user-import/{jobId}. */
  status: null,

  /** Set once the user closes the finished indicator. Only offered on a terminal job. */
  isDismissed: false,

  /** True while the drawer is showing, so the indicator can stand down rather than double up. */
  isDrawerOpen: false,

  // ────────── ACTIONS ──────────

  // Status is cleared alongside the new id: keeping the previous job's response would render the
  // new import as already terminal until the first poll landed.
  trackJob: (jobId) => set({ jobId, status: null, isDismissed: false }),

  setStatus: (status) => set({ status }),

  dismiss: () => set({ isDismissed: true }),

  setDrawerOpen: (isDrawerOpen) => set({ isDrawerOpen }),

  // Called when the user acknowledges the result (Done) and on logout, so a job never carries
  // over to the next user of the browser.
  clear: () => set({ jobId: null, status: null, isDismissed: false, isDrawerOpen: false }),
}));
